import React from 'react';
import { useTranslation } from 'react-i18next';
import { DOM, isBrowser } from '@app/utils';
import type { LegacyProduct } from '@app/types';
import { useInitiateUserLogin, useUserIsLoggedIn } from '@app/user';
import { HeartLightIcon, HeartDarkIcon } from '@stockx/icons';
import {
  Box,
  chakra,
  IconButton,
  useDisclosure,
  useBreakpointValue,
} from '@stockx/ui';
import {
  FAVORITE_PRODUCT_REDIRECT_KEY,
  IFavoriteRedirectData,
} from '../../constants';
import { Product } from '../../types';
import Modal from '../Modal';
import Drawer from '../Drawer';
import FavoriteError from '../FavoriteError';
import { useProductIsFavorited, useFavoriteVariants } from '../../hooks';

interface IFavoriteFromCellProps {
  product: Product | Partial<LegacyProduct>;
}
const FavoriteWrapper = chakra(Box, {
  baseStyle: {
    position: 'absolute',
    top: 0,
    right: 0,
  },
});

const FavoriteFromCell = ({ product }: IFavoriteFromCellProps) => {
  const { t } = useTranslation();
  const { isMobile } = useBreakpointValue();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const isLoggedIn = useUserIsLoggedIn();
  const initiateUserLogin = useInitiateUserLogin();
  const productCell = product as Product;
  // product.productVariant is for BidsAsks Carousel
  const favoritedProduct = useProductIsFavorited(
    productCell?.productVariant?.product?.id
      ? productCell?.productVariant?.product?.id
      : productCell?.id,
    productCell?.favorite,
  );

  const {
    handleFavoriteUpdate,
    favoriteLoading,
    unfavoriteLoading,
    setRetry,
    modalErrorIsOpen,
    modalErrorOnClose,
    modalErrorOnOpen,
  } = useFavoriteVariants({ product: productCell, onOpen, favoritedProduct });

  const hasVariants = productCell?.variants
    ? productCell?.variants?.length > 1
    : false;

  const onAnonymousUserClick = () => {
    if (isBrowser) {
      const redirectData: IFavoriteRedirectData = {
        redirectUrl: product.urlKey,
        productUuid: product.uuid,
        shouldOpenFavoriteModal: true,
      };

      DOM.setCookie(FAVORITE_PRODUCT_REDIRECT_KEY, redirectData);
    }

    initiateUserLogin();
  };

  const handleOnClick = async () => {
    if (isLoggedIn && hasVariants) {
      onOpen();
    } else if (isLoggedIn && !hasVariants) {
      handleFavoriteUpdate();
    } else {
      onAnonymousUserClick();
    }
  };

  return (
    <FavoriteWrapper>
      <IconButton
        data-testid="TriggerFavoriteView"
        onClick={handleOnClick}
        aria-label={t('pdp_follow_aria_label')}
        icon={
          favoritedProduct && isLoggedIn ? (
            <HeartDarkIcon />
          ) : (
            <HeartLightIcon />
          )
        }
        size="lg"
      />
      {isOpen && hasVariants && (
        <>
          {!isMobile ? (
            <Modal
              isOpen={isOpen}
              onClose={onClose}
              product={productCell}
              favoritedProduct={favoritedProduct}
              errorOnOpen={modalErrorOnOpen}
            />
          ) : (
            <Drawer
              isOpen={isOpen}
              onClose={onClose}
              product={productCell}
              favoritedProduct={favoritedProduct}
              errorOnOpen={modalErrorOnOpen}
            />
          )}
        </>
      )}
      {modalErrorIsOpen && (
        <FavoriteError
          isOpen={modalErrorIsOpen}
          onClose={modalErrorOnClose}
          isLoading={favoriteLoading || unfavoriteLoading}
          setRetry={setRetry}
        />
      )}
    </FavoriteWrapper>
  );
};

export default FavoriteFromCell;
